<template>
  <main class="container-1 terrain-campaign-edit">
    <div class="container-header">
      <Breadcrumb :links="routesBread" />
      <h2>{{ isEditing ? 'Editar campaña' : 'Nueva campaña' }}</h2>
    </div>

    <section class="container-body terrain-campaign-edit__body" aria-live="polite">
      <p v-if="loading" class="terrain-campaign-edit__status">Cargando campaña…</p>

      <template v-else>
        <div class="terrain-campaign-edit__header">
          <div class="terrain-campaign-edit__header-status">
            <TerrainBadge v-if="isEditing" :type="statusBadgeType" :message="statusLabel" />
          </div>
          <div class="terrain-campaign-edit__actions">
            <template v-if="canShareLink">
              <button type="button" class="terrain-campaign-edit__secondary" @click="openShareModal">Enlace</button>
              <button
                v-if="hasShareApi"
                type="button"
                class="terrain-campaign-edit__icon-btn"
                title="Compartir enlace"
                aria-label="Compartir enlace"
                @click="shareShareableLink"
              >
                <font-awesome-icon icon="share" />
              </button>
            </template>
            <button type="button" class="terrain-campaign-edit__primary" :disabled="editLocked || saving || !hasUnsavedChanges" @click="onSave">
              {{ saving ? 'Guardando…' : 'Guardar' }}
            </button>
            <button
              v-if="status === 'draft'"
              type="button"
              class="terrain-campaign-edit__secondary"
              :disabled="editLocked || publishing || saving"
              @click="confirmPublish"
            >
              {{ publishing ? 'Guardando y publicando…' : (hasUnsavedChanges || !isEditing ? 'Guardar y publicar' : 'Publicar') }}
            </button>
            <button
              v-if="isEditing && status === 'published'"
              type="button"
              class="terrain-campaign-edit__secondary"
              :disabled="closing"
              @click="confirmClose"
            >
              {{ closing ? 'Cerrando…' : 'Cerrar campaña' }}
            </button>
            <button
              v-if="isEditing"
              type="button"
              class="terrain-campaign-edit__secondary"
              :disabled="duplicating"
              @click="onDuplicate"
            >
              {{ duplicating ? 'Duplicando…' : 'Duplicar como nueva' }}
            </button>
          </div>
        </div>

        <modal name="terrain-campaign-share-link" :width="420" height="auto" :adaptive="true">
          <div class="terrain-campaign-edit__share-modal">
            <h2>Enlace compartible</h2>
            <p class="terrain-campaign-edit__hint">Concede acceso según el modo configurado en Destinatarios.</p>
            <div class="terrain-campaign-edit__share-row">
              <input class="terrain-campaign-edit__share-link" :value="shareableLink" readonly>
              <button type="button" class="terrain-campaign-edit__secondary" @click="copyShareableLink">Copiar</button>
            </div>
            <div class="terrain-campaign-edit__map-modal-actions">
              <button type="button" class="terrain-campaign-edit__map-modal-cancel" @click="closeShareModal">Cerrar</button>
            </div>
          </div>
        </modal>

        <modal name="terrain-template-preview" :width="820" height="90%" :adaptive="true" :scrollable="true">
          <div class="terrain-campaign-edit__template-preview">
            <header class="terrain-campaign-edit__template-preview-header">
              <div>
                <h2>Vista previa de plantilla</h2>
                <p>{{ selectedTemplateLabel }} · {{ selectedTemplateVersionLabel }}</p>
              </div>
              <button type="button" class="terrain-campaign-edit__icon-btn" aria-label="Cerrar vista previa" title="Cerrar" @click="closeTemplatePreview">
                <font-awesome-icon icon="times" />
              </button>
            </header>
            <div class="terrain-campaign-edit__template-preview-body">
              <p v-if="templatePreviewLoading" class="terrain-campaign-edit__status">Cargando plantilla…</p>
              <div v-else-if="templatePreviewError" class="terrain-campaign-edit__template-preview-error" role="alert">
                <p>{{ templatePreviewError }}</p>
                <button type="button" class="terrain-campaign-edit__secondary" @click="loadTemplatePreview">Reintentar</button>
              </div>
              <TerrainFormRenderer
                v-else-if="templatePreviewDefinition"
                :definition="templatePreviewDefinition"
                :answers="[]"
                mode="preview"
                readonly
                show-all-terrain-elements
              />
            </div>
          </div>
        </modal>

        <p v-if="errorMessage" class="terrain-campaign-edit__error" role="alert">{{ errorMessage }}</p>
        <ul v-if="fieldErrorMessages.length" class="terrain-campaign-edit__error" role="alert">
          <li v-for="(message, index) in fieldErrorMessages" :key="index">{{ message }}</li>
        </ul>

        <label class="terrain-campaign-edit__field">
          Nombre de la campaña
          <input v-model="name" type="text" :disabled="disabled">
        </label>

        <div class="terrain-campaign-edit__row">
          <SearchSelect
            label="Plantilla"
            :value="templateId"
            :options="templateOptions"
            :loading="templatesLoading"
            :disabled="disabled || templateLocked"
            placeholder="Buscar plantilla…"
            empty-message="No hay plantillas creadas"
            @input="onTemplateChange"
          />
          <SearchSelect
            label="Versión publicada"
            :value="templateVersionId"
            :options="templateVersionOptions"
            :loading="templateVersionsLoading"
            :disabled="disabled || templateLocked || !templateId"
            placeholder="Seleccione una versión…"
            empty-message="Esta plantilla no tiene versiones publicadas"
            @input="onTemplateVersionChange"
          />
          <div class="terrain-campaign-edit__preview-action">
            <button
              type="button"
              class="terrain-campaign-edit__secondary terrain-campaign-edit__preview-button"
              :disabled="!templateVersionId || templateVersionsLoading"
              @click="openTemplatePreview"
            >
              <font-awesome-icon icon="eye" /> Ver plantilla
            </button>
          </div>
        </div>
        <p v-if="templateLocked" class="terrain-campaign-edit__hint">
          La campaña ya tiene respuestas, por lo que su configuración no se puede modificar.
        </p>

        <div class="terrain-campaign-edit__row">
          <TerrainDateRangePicker
            :start="startDay"
            :end="endDay"
            label="Vigencia de la campaña"
            @update:start="startDay = $event"
            @update:end="endDay = $event"
          />
        </div>

        <div class="terrain-campaign-edit__row">
          <label class="terrain-campaign-edit__field terrain-campaign-edit__field--narrow">
            Máximo de respuestas por respondente
            <span class="terrain-campaign-edit__hint">Vacío = respuestas ilimitadas</span>
            <input v-model.number="maxPerRespondent" type="number" min="1" :disabled="disabled">
          </label>

          <label class="terrain-campaign-edit__checkbox-field">
            <input v-model="requiresApproval" type="checkbox" :disabled="disabled">
            Requiere aprobación manual de las respuestas
          </label>
        </div>
        <p v-if="!requiresApproval" class="terrain-campaign-edit__hint">
          Cada respuesta enviada quedará aprobada y visible en el mapa de inmediato, sin pasar por revisión.
        </p>

        <fieldset class="terrain-campaign-edit__fieldset">
          <legend>Destinatarios</legend>
		  <label class="terrain-campaign-edit__field terrain-campaign-edit__field--narrow">
			Acceso del enlace compartible
			<TerrainSelect :value="sharedAccessMode" :options="sharedAccessModeOptions" :disabled="disabled" aria-label="Acceso del enlace compartible" @input="sharedAccessMode = $event" />
		  </label>
		  <p v-if="sharedAccessMode === 'open'" class="terrain-campaign-edit__hint">Este enlace concede acceso inmediato después de ingresar un correo; no verifica que la persona sea su propietaria.</p>
          <RecipientPicker v-model="selectedSources" :api="terrainApi" :disabled="disabled" :show-preview="status !== 'published'" :active-emails="activeRecipientEmails" />
        </fieldset>

		<fieldset class="terrain-campaign-edit__fieldset">
		  <legend>Formularios prellenados</legend>
		  <label class="terrain-campaign-edit__checkbox-field"><input v-model="prefillEnabled" type="checkbox" :disabled="disabled" @change="onPrefillToggle"> Habilitar bandeja de casos prellenados</label>
		  <p class="terrain-campaign-edit__hint">El prellenado requiere ingreso controlado. Los campos bloqueados se vuelven a imponer en el servidor.</p>
		  <template v-if="prefillEnabled">
			<SearchSelect
			  label="Pregunta identificadora"
			  :value="identifierQuestionId"
			  :options="prefillIdentifierQuestions"
			  id-key="questionId"
			  :loading="templatePreviewLoading"
			  :disabled="disabled"
			  :clearable="false"
			  placeholder="Buscar pregunta…"
			  :empty-message="identifierEmptyMessage"
			  @input="identifierQuestionId = $event"
			/>
			<p v-if="isEditing && identifierQuestionId && !hasUnsavedChanges" class="terrain-campaign-edit__hint">Elegí qué preguntas más son prellenables (y cuáles quedan bloqueadas) desde "Administrar casos e importaciones".</p>
			<router-link v-if="isEditing && !hasUnsavedChanges" :to="{ name: 'terrain-prefill-manage', params: { campaignId } }">Administrar casos e importaciones</router-link>
			<p v-else-if="isEditing" class="terrain-campaign-edit__hint">Guarde los cambios antes de administrar casos e importaciones.</p>
		  </template>
		</fieldset>

        <fieldset v-if="isEditing && status === 'published'" class="terrain-campaign-edit__fieldset">
          <legend>Destinatarios de la campaña publicada</legend>

          <div v-if="newMembers.length" class="terrain-campaign-edit__new-members">
            <p>
              {{ newMembers.length }} nuevo{{ newMembers.length === 1 ? '' : 's' }}
              integrante{{ newMembers.length === 1 ? '' : 's' }} detectado{{ newMembers.length === 1 ? '' : 's' }}
              en los grupos/cuadrillas seleccionados, todavía sin acceso a esta campaña:
            </p>
            <ul>
              <li v-for="member in newMembers" :key="member.userId">
                {{ member.username }}
                <span class="terrain-campaign-edit__hint">({{ member.sources.map((s) => s.sourceNameSnapshot).join(', ') }})</span>
              </li>
            </ul>
            <button type="button" class="terrain-campaign-edit__secondary" :disabled="expanding" @click="confirmExpand">
              {{ expanding ? 'Enviando…' : 'Enviar a nuevos integrantes' }}
            </button>
          </div>
          <p v-else-if="!newMembersLoading" class="terrain-campaign-edit__hint">No hay nuevos integrantes pendientes de incorporar.</p>

          <p v-if="recipientsLoading" class="terrain-campaign-edit__status">Cargando destinatarios…</p>
          <table v-else-if="recipients.length" class="terrain-campaign-edit__notifications">
            <thead>
              <tr>
                <th scope="col">Destinatario</th>
                <th scope="col">Fuente</th>
                <th scope="col">Estado</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="recipient in recipients">
                <tr :key="recipient.id">
                  <td>{{ recipientLabel(recipient) }}</td>
                  <td>{{ recipient.sources.map((s) => s.sourceNameSnapshot).join(', ') }}</td>
                  <td>
                    <TerrainBadge :type="recipient.status === 'active' ? 'success' : 'secondary'" :message="recipient.status === 'active' ? 'Activo' : 'Retirado'" />
                  </td>
                  <td>
                    <button
                      v-if="recipient.status === 'active'"
                      type="button"
                      class="terrain-campaign-edit__secondary"
                      :disabled="withdrawing"
                      @click="startWithdraw(recipient)"
                    >Retirar</button>
                  </td>
                </tr>
				<tr v-if="withdrawTargetId === recipient.id" :key="`${recipient.id}-withdraw`">
                  <td colspan="4" class="terrain-campaign-edit__withdraw-row">
                    <label class="terrain-campaign-edit__field">
                      Motivo del retiro (obligatorio)
                      <textarea v-model="withdrawReason" rows="2"></textarea>
                    </label>
                    <div class="terrain-campaign-edit__actions">
                      <button type="button" class="terrain-campaign-edit__primary" :disabled="withdrawing" @click="confirmWithdraw(recipient)">
                        {{ withdrawing ? 'Retirando…' : 'Confirmar retiro' }}
                      </button>
                      <button type="button" class="terrain-campaign-edit__secondary" :disabled="withdrawing" @click="cancelWithdraw">Cancelar</button>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <p v-else class="terrain-campaign-edit__hint">Todavía no hay destinatarios incorporados.</p>
        </fieldset>

        <fieldset class="terrain-campaign-edit__fieldset">
          <legend>Mapas de destino</legend>
          <TagMultiSelect
            v-model="destinationMapIds"
            :options="mapOptions"
            :loading="mapsLoading"
            :disabled="disabled"
            placeholder="Buscar mapas…"
            empty-message="Sin mapas disponibles"
          />
        </fieldset>

        <fieldset v-if="destinationMapIds.length" class="terrain-campaign-edit__fieldset">
          <legend>
            <button type="button" class="terrain-campaign-edit__accordion-toggle" :aria-expanded="mapStyleSectionOpen"
              aria-label="Mostrar u ocultar Estilo por mapa" @click="mapStyleSectionOpen = !mapStyleSectionOpen">
              Estilo por mapa
              <font-awesome-icon :icon="mapStyleSectionOpen ? 'chevron-up' : 'chevron-down'" />
            </button>
          </legend>
          <transition name="expand">
          <div v-show="mapStyleSectionOpen">
          <p v-if="status === 'published' && terrainLayerId" class="terrain-campaign-edit__hint">Cada mapa de destino personaliza cómo se ve esta Capa_Terreno de forma independiente — cambiar el estilo u orden no modifica el GeoJSON ni las respuestas.</p>
          <p v-else class="terrain-campaign-edit__hint">Este estilo queda guardado como borrador y se aplica a la Capa_Terreno recién al publicar la campaña.</p>

          <div v-for="mapId in destinationMapIds" :key="mapId" class="terrain-campaign-edit__map-style-card">
            <h4>{{ mapLabel(mapId) }}</h4>
            <p v-if="!mapLayerSettings[mapId]" class="terrain-campaign-edit__status">Cargando…</p>
            <template v-else>
              <div class="terrain-campaign-edit__row">
                <SearchSelect
                  label="Grupo visual"
                  :value="mapLayerSettings[mapId].visualGroupId"
                  :options="visualGroupOptionsFor(mapId)"
                  :loading="visualGroupOptionsLoading[mapId]"
                  :disabled="mapLayerSettings[mapId].saving"
                  :clearable="false"
                  allow-create
                  placeholder="Buscar o crear grupo…"
                  @input="(id) => selectVisualGroup(mapId, id)"
                  @create="(text) => createVisualGroup(mapId, text)"
                />
                <label class="terrain-campaign-edit__field terrain-campaign-edit__field--narrow">
                  Orden del grupo
                  <input v-model.number="mapLayerSettings[mapId].visualGroupOrder" type="number" :disabled="mapLayerSettings[mapId].saving">
                </label>
                <label class="terrain-campaign-edit__field terrain-campaign-edit__field--narrow">
                  Orden de la capa
                  <input v-model.number="mapLayerSettings[mapId].layerOrder" type="number" :disabled="mapLayerSettings[mapId].saving">
                </label>
              </div>
              <div class="terrain-campaign-edit__row">
                <label class="terrain-campaign-edit__field terrain-campaign-edit__field--narrow">
                  Color
                  <input v-model="mapLayerSettings[mapId].color" type="color" :disabled="mapLayerSettings[mapId].saving">
                </label>
                <label class="terrain-campaign-edit__field terrain-campaign-edit__field--narrow">
                  Ícono
                  <TerrainSelect
                    :value="mapLayerSettings[mapId].icon"
                    :options="iconOptions"
                    :disabled="mapLayerSettings[mapId].saving"
                    aria-label="Ícono"
                    @input="mapLayerSettings[mapId].icon = $event"
                  />
                </label>
                <label class="terrain-campaign-edit__field terrain-campaign-edit__field--narrow">
                  Tamaño
                  <input v-model.number="mapLayerSettings[mapId].size" type="number" min="4" max="48" :disabled="mapLayerSettings[mapId].saving">
                </label>
                <label class="terrain-campaign-edit__field terrain-campaign-edit__field--narrow">
                  Opacidad
                  <input v-model.number="mapLayerSettings[mapId].opacity" type="number" min="0" max="1" step="0.1" :disabled="mapLayerSettings[mapId].saving">
                </label>
              </div>
              <label class="terrain-campaign-edit__checkbox-field">
                <input v-model="mapLayerSettings[mapId].initialVisible" type="checkbox" :disabled="mapLayerSettings[mapId].saving">
                Visible al abrir el mapa
              </label>
              <div v-if="status === 'published' && terrainLayerId" class="terrain-campaign-edit__actions">
                <button type="button" class="terrain-campaign-edit__primary" :disabled="mapLayerSettings[mapId].saving" @click="saveMapLayerSettings(mapId)">
                  {{ mapLayerSettings[mapId].saving ? 'Guardando…' : 'Guardar estilo' }}
                </button>
              </div>
            </template>
          </div>
          </div>
          </transition>
        </fieldset>

      </template>
    </section>
  </main>
</template>

<script>
import Breadcrumb from "@/components/Breadcrumb.vue";
import TerrainBadge from "@/components/terrain/TerrainBadge.vue";
import TerrainDateRangePicker from "@/components/terrain/TerrainDateRangePicker.vue";
import SearchSelect from "@/components/common/pickers/SearchSelect.vue";
import TagMultiSelect from "@/components/common/pickers/TagMultiSelect.vue";
import TerrainSelect from "@/components/terrain/TerrainSelect.vue";
import TerrainFormRenderer from "@/components/terrain/form/TerrainFormRenderer.vue";
import RecipientPicker from "@/components/common/pickers/RecipientPicker.vue";
import { createTerrainApi } from "@/services/terrain/api.js";

// Solo estos 8 eventos están realmente cableados en el backend
// (repository/terrain/campaign_publication_repo.go,
// services/terrain/review_workflow.go::reviewNotificationEvent,
// services/terrain/expand_recipients.go, services/terrain/submission_service.go
// y services/terrain/campaign_lifecycle.go). El backend no restringe las
// claves de notificationPolicy, pero ofrecer toggles para eventos no
// disparados ("inicio" no es ni siquiera un evento real: no hay disparador,
// solo una ventana de fechas evaluada en cada request) sería un control
// muerto — ver decisión en thoughts/shared/plans/2026-07-23_PB-24.md.
//
// De estos 8, "published", "approved" y "new_member" avisan al respondente
// (campanita + correo opcional): un aviso por cada observación/rechazo/
// retiro es ruido, no señal, para quien responde, así que
// "observed"/"rejected"/"withdrawn" solo alimentan el recibo interno de
// auditoría (terrain_notifications, sin UI propia) — por eso no ofrecen la
// columna "Correo", que ahí no tendría ningún efecto. "submitted" y
// "closed" en cambio avisan al equipo administrador/revisor (todo usuario
// con capacidad manage o review, no hay asignación de revisores por
// Campaña), así que sí ofrecen ambos canales.
const NOTIFICATION_EVENTS = [
  { key: "published", label: "Publicación de la campaña", channels: ["internal", "email"] },
  { key: "new_member", label: "Incorporación de nuevo integrante", channels: ["internal", "email"] },
  { key: "submitted", label: "Nueva respuesta recibida (al equipo revisor)", channels: ["internal", "email"] },
  { key: "closed", label: "Cierre de la campaña (al equipo administrador)", channels: ["internal", "email"] },
  { key: "observed", label: "Observación de respuesta", channels: ["internal"] },
  { key: "approved", label: "Aprobación de respuesta", channels: ["internal", "email"] },
  { key: "rejected", label: "Rechazo de respuesta", channels: ["internal"] },
  { key: "withdrawn", label: "Retiro de respuesta", channels: ["internal"] },
];

// Catálogo de íconos permitidos para el estilo de una Capa_Terreno en el
// mapa (tarea 13.1) — debe reflejar exactamente
// services/terrain/map_layer_settings.go::terrainLayerIconCatalog en el
// backend, que es quien realmente lo valida; esta lista es solo para no
// ofrecer en el selector algo que el backend va a rechazar.
// Estos valores son "well-known names" de SLD (OGC PointSymbolizer), no
// íconos de FontAwesome: el mapa renderiza Capa_Terreno como WMS de
// GeoServer y su estilo se arma en el cliente como SLD_BODY
// (src/services/layers/layerService.js) — un PointSymbolizer solo puede
// dibujar esta lista fija de formas, así que el selector se restringe a lo
// mismo que realmente se puede renderizar, en vez de ofrecer un ícono
// (ej. un pin o una flecha) que GeoServer no tiene forma de dibujar.
const ICON_CATALOG = [
  { value: "circle", label: "Círculo" },
  { value: "square", label: "Cuadrado" },
  { value: "triangle", label: "Triángulo" },
  { value: "star", label: "Estrella" },
  { value: "cross", label: "Cruz" },
  { value: "x", label: "X" },
];
const DEFAULT_TERRAIN_MARKER_SIZE = 24;

const NOTIFICATION_EVENTS_WITH_EMAIL_DEFAULT = new Set(["published", "new_member", "closed"]);

function defaultNotificationPolicy() {
  const policy = {};
  // "published"/"new_member" son la forma en que un destinatario se entera
  // de que puede acceder a la campaña, y "closed" es un evento único por
  // Campaña con impacto real (el equipo administrador debe saber que ya no
  // se aceptan envíos): los tres van con correo activado por defecto.
  // "submitted" en cambio puede repetirse muchas veces por Campaña activa
  // — un correo por cada respuesta sería ruidoso, así que queda solo con
  // la campanita interna hasta que el administrador active el correo a
  // propósito.
  NOTIFICATION_EVENTS.forEach(({ key }) => { policy[key] = { internal: true, email: NOTIFICATION_EVENTS_WITH_EMAIL_DEFAULT.has(key) }; });
  return policy;
}

// La campaña ahora es por día completo, en la zona fija America/Santiago
// (Big Boss: el selector de zona horaria no aportaba valor). El backend
// (campaign_service.go::parseCampaignDay) interpreta startAt/closeAt como
// YYYY-MM-DD en esa zona y calcula los instantes UTC exactos — acá solo se
// hidrata el día calendario a mostrar en TerrainDateRangePicker, vía Intl
// (sin matemática de offset/DST manual, eso ya lo resuelve el navegador).
function toSantiagoDay(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Santiago" }).format(date);
}

export default {
  name: "TerrainCampaignEdit",
  components: { Breadcrumb, TerrainBadge, TerrainDateRangePicker, SearchSelect, TagMultiSelect, TerrainSelect, TerrainFormRenderer, RecipientPicker },
  data() {
    return {
      loading: false,
      saving: false,
      publishing: false,
      closing: false,
      duplicating: false,
      errorMessage: "",
      fieldErrorMessages: [],
      resourceVersion: 0,
      status: "draft",
      hasResponses: false,

      name: "",
      templateId: null,
      templateVersionId: null,
      templateOptions: [],
      templatesLoading: false,
      templateVersionOptions: [],
      templateVersionsLoading: false,
      templatePreviewDefinition: null,
      templatePreviewVersionId: null,
      templatePreviewLoading: false,
      templatePreviewError: "",

      startDay: "",
      endDay: "",

      maxPerRespondent: null,
      requiresApproval: true,
	  sharedAccessMode: "restricted",
	  publicAccessId: "",
	  prefillEnabled: false,
	  identifierQuestionId: '',
	  prefillPolicies: {},

      selectedSources: {
		emails: [], excludedEmails: [], userIds: [], groupIds: [], crewIds: [], excludedUserIds: [],
      },

      destinationMapIds: [],
      mapOptions: [],
      mapsLoading: false,

      notificationPolicy: defaultNotificationPolicy(),

      recipients: [],
      recipientsLoading: false,
      newMembers: [],
      newMembersLoading: false,
      expanding: false,
      withdrawTargetId: null,
      withdrawReason: "",
      withdrawing: false,

      terrainLayerId: null,
      mapLayerSettings: {},
      // mapStyleDrafts: el estilo por mapa tal cual viene en la respuesta de
      // la campaña (campaign.destinationMapStyleDrafts) mientras todavía es
      // borrador — fuente para loadMapLayerSettings() antes de publicar,
      // separado de mapLayerSettings (que es el estado editable del
      // formulario, ya sea que la fuente real sea este borrador o la fila
      // de terrain_map_layer_settings tras publicar).
      mapStyleDrafts: {},
      // visualGroupOptions: mapId -> [{id, label}], los grupos visuales que
      // ya usan otras capas terreno (de cualquier campaña) asignadas a ese
      // mapa (GET /maps/:mapId/section) — catálogo para el combobox
      // creable de "Grupo visual", ver loadVisualGroupOptions().
      visualGroupOptions: {},
      visualGroupOptionsLoading: {},
      iconOptions: ICON_CATALOG,
      // Estilo por mapa arranca cerrado (acordeón) — es una sección de ajuste
      // fino que no todos los editores necesitan tocar cada vez que entran.
      mapStyleSectionOpen: false,

      // baselinePayload es el JSON de buildPayload() tal como quedó tras la
      // última carga/guardado exitoso — hasUnsavedChanges lo compara contra
      // el payload actual para que Guardar/Guardar y publicar arranquen
      // deshabilitados y solo se habiliten una vez que algo realmente
      // cambió (Big Boss: evita guardados vacíos / republicar sin motivo).
      // null mientras no se haya capturado todavía (loading inicial).
      baselinePayload: null,
    };
  },
  computed: {
    // Correos ya en la tabla "Destinatarios de la campaña publicada" (solo
    // activos: uno retirado no debe seguir tapando su chip de "pendiente" en
    // RecipientPicker) — le dice al picker qué NO repetir en sus chips.
    activeRecipientEmails() { return this.recipients.filter((r) => r.status === "active").map((r) => r.email).filter(Boolean); },
    campaignId() { return this.$route.params.campaignId || null; },
    isEditing() { return Boolean(this.campaignId); },
    // Edición libre para cualquier status: el backend (campaign_service.go
    // UpdateCampaign) no restringe por status, solo por resourceVersion.
    // "Destinatarios" (RecipientPicker) edita las selecciones del borrador
    // (Grupos/Cuadrillas/usuarios); no vuelve a congelar ni amplía la Lista_
    // Efectiva_de_Destinatarios ya publicada — eso solo lo hace la sección
    // "Destinatarios de la campaña publicada" de más abajo (tareas 4.4-4.6).
    disabled() { return this.saving || this.loading || this.hasResponses; },
    editLocked() { return this.hasResponses; },
    // La campaña completa se bloquea desde su primera respuesta. Antes de
    // eso sigue siendo configurable, incluso si ya fue publicada.
    templateLocked() { return this.hasResponses; },
    routesBread() {
      return [
        { name: "Terreno" },
        { name: "Campañas", path: "/terreno/campanas" },
        { name: this.isEditing ? "Editar" : "Nueva" },
      ];
    },
    terrainApi() { return createTerrainApi(this.$http, this.$store.getters.backendurl); },
	selectedTemplateLabel() {
	  const selected = this.templateOptions.find((option) => option.id === this.templateId);
	  return selected ? selected.label : "Plantilla seleccionada";
	},
	selectedTemplateVersionLabel() {
	  const selected = this.templateVersionOptions.find((option) => option.id === this.templateVersionId);
	  return selected ? selected.label : "Versión publicada";
	},
	shareableLink() { return this.publicAccessId ? `${window.location.origin}/#/terreno/acceso/${this.publicAccessId}` : ""; },
	// Solo tiene sentido ofrecer el botón/modal de enlace cuando ya hay algo
	// que compartir (campaña publicada, con publicAccessId ya generado) —
	// mismo criterio de gateo que "Abrir enlace"/compartir/copiar en la
	// lista de campañas (TerrainCampaigns.vue::isActive).
	canShareLink() { return this.status === "published" && !!this.publicAccessId; },
	hasShareApi() { return typeof navigator !== "undefined" && !!navigator.share; },
	sharedAccessModeOptions() {
	  return [
	    { value: "restricted", label: "Ingreso controlado" },
	    { value: "open", label: "Ingreso libre" },
	  ];
	},
	prefillQuestions() {
	  const allowed = new Set(['shortText', 'longText', 'number', 'date', 'singleSelect', 'multiSelect', 'check']);
	  return (((this.templatePreviewDefinition || {}).sections) || []).reduce((all, section) => all.concat((section.questions || []).filter((question) => allowed.has(question.type))), []);
	},
	prefillIdentifierQuestions() {
	  const allowed = new Set(['shortText', 'longText', 'number', 'date', 'singleSelect', 'check']);
	  const visibleSections = new Set((((this.templatePreviewDefinition || {}).sections) || []).filter((section) => !section.visibility).map((section) => section.sectionId));
	  return this.prefillQuestions.filter((question) => allowed.has(question.type) && !question.visibility && (((this.templatePreviewDefinition || {}).sections) || []).some((section) => section.sectionId && visibleSections.has(section.sectionId) && (section.questions || []).some((item) => item.questionId === question.questionId)));
	},
	identifierEmptyMessage() {
	  if (!this.templateVersionId) return "Elegí primero una versión de plantilla.";
	  if (this.templatePreviewLoading) return "Cargando preguntas…";
	  return "Esta plantilla no tiene ninguna pregunta que pueda usarse como identificadora (debe ser texto corto, texto largo, número, fecha, selección simple o casilla, siempre visible en una sección también siempre visible). Editá la plantilla o elegí otra versión.";
	},
    statusLabel() { return ({ draft: "Borrador", published: "Publicada", closed: "Cerrada" })[this.status] || this.status; },
    statusBadgeType() { return ({ draft: "secondary", published: "success", closed: "warning" })[this.status] || "secondary"; },
    // hasUnsavedChanges compara el payload actual contra la última
    // instantánea capturada (captureBaseline, tras cargar/guardar) para
    // habilitar Guardar/Guardar y publicar solo cuando hay algo nuevo que
    // enviar. null (todavía sin capturar, ej. mientras loading) cuenta como
    // "sin cambios" — evita un parpadeo habilitado antes de tiempo.
    hasUnsavedChanges() {
      if (this.baselinePayload === null) return false;
      return JSON.stringify(this.buildPayload()) !== this.baselinePayload;
    },
  },
  created() { this.initialize(); },
  watch: {
	// Guardado automático del prellenado: Big Boss reportó que exigir un
	// click a "Guardar" (aparte de tildar el checkbox) antes de poder entrar
	// a "Administrar casos" era fricción innecesaria — acá la campaña ya
	// existe y ya pasó su primera validación, así que autoguardar en cuanto
	// la config de prellenado queda completa (habilitado + pregunta
	// identificadora) es seguro: no hay campos ajenos a medio llenar que
	// puedan romper la validación general del formulario.
	identifierQuestionId(value) { if (this.prefillEnabled && value) this.$set(this.prefillPolicies, value, true); this.scheduleAutoSavePrefill(); },
    // Vue Router reutiliza esta misma instancia al navegar entre
    // /campanas/nueva ↔ /:campaignId/editar ↔ otro /:campaignId/editar (ej.
    // al duplicar) — sin este watcher, created() no vuelve a dispararse y el
    // formulario mostraría los datos de la campaña anterior.
    "$route.params.campaignId"() { this.initialize(); },
    // "Estilo por mapa" ahora está disponible desde que se eligen mapas de
    // destino, incluso creando la campaña por primera vez (antes requería
    // que ya estuviera publicada) — este watcher inicializa/actualiza las
    // tarjetas de estilo cada vez que la selección de mapas cambia, sin
    // depender de que loadCampaign() ya haya corrido.
    destinationMapIds() {
      if (this.destinationMapIds.length) {
        this.loadMapLayerSettings();
        this.destinationMapIds.forEach((mapId) => this.loadVisualGroupOptions(mapId));
      }
    },
  },
  methods: {
    async initialize() {
      this.baselinePayload = null;
      this.hasResponses = false;
      this.loadTemplateOptions();
      this.loadMapOptions();
      if (this.campaignId) await this.loadCampaign();
      // loadCampaign() dispara el watcher de destinationMapIds, pero no lo
      // espera (fire-and-forget) — se vuelve a llamar acá, esta vez
      // esperado, para que mapLayerSettings (parte de buildPayload() vía
      // buildMapStyleDraftsPayload()) ya esté poblado antes de capturar la
      // instantánea base; es incremental/idempotente, así que repetirlo no
      // pisa nada si el watcher ya corrió.
      await this.loadMapLayerSettings();
      await this.$nextTick();
      this.captureBaseline();
    },
    // captureBaseline fija el payload "ya guardado" vigente — hasUnsavedChanges
    // compara contra esto para saber si hay algo nuevo que enviar.
    captureBaseline() {
      this.baselinePayload = JSON.stringify(this.buildPayload());
    },
    async loadTemplateOptions() {
      this.templatesLoading = true;
      try {
        const response = await this.terrainApi.listTemplates({ page: 1, size: 100 });
        const items = (response.data && response.data.items) || [];
        this.templateOptions = items.map((template) => ({
          id: template.id,
          label: template.title || "Sin título",
          sublabel: template.latestPublishedVersion ? `v${template.latestPublishedVersion} publicada` : "Sin publicar",
        }));
      } catch (error) {
        this.templateOptions = [];
      } finally {
        this.templatesLoading = false;
      }
    },
    async loadMapOptions() {
      this.mapsLoading = true;
      const backendurl = this.$store.getters.backendurl;
      try {
        const response = await this.$http.get(`${backendurl}api/v1/maps/`);
        this.mapOptions = (response.data || []).map((map) => ({ id: map.id, label: map.name }));
      } catch (error) {
        this.mapOptions = [];
      } finally {
        this.mapsLoading = false;
      }
    },
    async loadTemplateVersions(templateId) {
      this.templateVersionsLoading = true;
      try {
        const response = await this.terrainApi.listTemplateVersions(templateId, { page: 1, size: 25 });
        const items = (response.data && response.data.items) || [];
        this.templateVersionOptions = items.map((version) => ({
          id: version.id,
          label: `Versión ${version.versionNumber}`,
          sublabel: this.dateLabel(version.publishedAt),
        }));
      } catch (error) {
        this.templateVersionOptions = [];
      } finally {
        this.templateVersionsLoading = false;
      }
    },
    async onTemplateChange(templateId) {
      this.templateId = templateId;
      this.templateVersionId = null;
      this.templateVersionOptions = [];
      this.clearTemplatePreview();
      if (templateId) await this.loadTemplateVersions(templateId);
    },
    onTemplateVersionChange(templateVersionId) {
      this.templateVersionId = templateVersionId;
      this.clearTemplatePreview();
      // Sin esto, templatePreviewDefinition solo se cargaba al abrir "Ver
      // plantilla" — y como prefillIdentifierQuestions depende de esa misma
      // definición, el select de pregunta identificadora quedaba vacío al
      // crear una campaña nueva hasta que el usuario abriera el preview a
      // mano (Big Boss, 2026-09-09: "no me deja seleccionar ninguna").
      this.loadTemplatePreview();
    },
    clearTemplatePreview() {
      this.templatePreviewDefinition = null;
      this.templatePreviewVersionId = null;
      this.templatePreviewLoading = false;
      this.templatePreviewError = "";
    },
    async openTemplatePreview() {
      if (!this.templateVersionId) return;
      this.$modal.show("terrain-template-preview");
      if (this.templatePreviewVersionId === this.templateVersionId && this.templatePreviewDefinition) return;
      await this.loadTemplatePreview();
    },
    async loadTemplatePreview() {
      if (!this.templateVersionId) return;
      const requestedVersionId = this.templateVersionId;
      this.templatePreviewLoading = true;
      this.templatePreviewError = "";
      try {
        const response = await this.terrainApi.getTemplateVersion(requestedVersionId);
        // Si el usuario cambió la versión mientras llegaba la respuesta, no
        // se muestra una definición que ya dejó de estar seleccionada.
        if (this.templateVersionId !== requestedVersionId) return;
        this.templatePreviewDefinition = response.data.definition;
        this.templatePreviewVersionId = requestedVersionId;
      } catch (error) {
        if (this.templateVersionId === requestedVersionId) this.templatePreviewError = "No fue posible cargar la vista previa de esta plantilla.";
      } finally {
        if (this.templateVersionId === requestedVersionId) this.templatePreviewLoading = false;
      }
    },
    closeTemplatePreview() { this.$modal.hide("terrain-template-preview"); },
    async loadCampaign() {
      this.loading = true;
      try {
        const response = await this.terrainApi.getCampaign(this.campaignId);
        const campaign = response.data;
        this.name = campaign.name;
        this.templateVersionId = campaign.templateVersionId;
        this.startDay = toSantiagoDay(campaign.startAt);
        // closeAt persistido es 00:00 del día SIGUIENTE al día de cierre
        // elegido (cierre exclusivo, ver campaign_service.go::parseCampaignDay)
        // — restar 1s antes de formatear cae de vuelta en el día de cierre
        // real, tanto para campañas nuevas (closeAt exacto a medianoche)
        // como legacy (creadas con el datetime-local viejo, hora arbitraria
        // que nunca cae justo en medianoche salvo coincidencia).
        this.endDay = toSantiagoDay(new Date(new Date(campaign.closeAt).getTime() - 1000));
        this.maxPerRespondent = (campaign.responsePolicy && campaign.responsePolicy.maxPerRespondent) || null;
        this.requiresApproval = campaign.requiresApproval !== false;
		this.sharedAccessMode = campaign.sharedAccessMode || "restricted";
		this.publicAccessId = campaign.publicAccessId || "";
		this.prefillEnabled = !!(campaign.prefill && campaign.prefill.enabled);
		this.identifierQuestionId = (campaign.prefill && campaign.prefill.identifierQuestionId) || '';
		this.prefillPolicies = ((campaign.prefill && campaign.prefill.fields) || []).reduce((result, field) => ({ ...result, [field.questionId]: field.editable !== false }), {});
        this.selectedSources = {
		  emails: [],
		  excludedEmails: [],
		  userIds: [],
          groupIds: [],
          crewIds: [],
          excludedUserIds: [],
          ...(campaign.selectedSources || {}),
        };
        this.notificationPolicy = { ...defaultNotificationPolicy(), ...(campaign.notificationPolicy || {}) };
        this.resourceVersion = campaign.resourceVersion;
        this.status = campaign.status;
        await this.loadResponseLock();
        this.terrainLayerId = campaign.terrainLayerId || null;
        this.mapStyleDrafts = campaign.destinationMapStyleDrafts || {};
        // Al final: dispara el watcher de destinationMapIds recién con
        // status/terrainLayerId/mapStyleDrafts ya al día, para que
        // loadMapLayerSettings() elija la fuente correcta (fila real vs.
        // borrador) desde el primer llamado.
        this.destinationMapIds = campaign.destinationMapIds || [];

        if (this.templateVersionId) {
          const versionResponse = await this.terrainApi.getTemplateVersion(this.templateVersionId);
          this.templateId = versionResponse.data.templateId;
          this.templatePreviewDefinition = versionResponse.data.definition;
          this.templatePreviewVersionId = this.templateVersionId;
          await this.loadTemplateVersions(this.templateId);
        }
        if (this.status === "published") {
          this.loadRecipients();
          this.loadNewMembers();
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.replace({ name: "inicio", query: { returnTo: this.$route.fullPath } });
          return;
        }
        this.errorMessage = "No fue posible cargar la campaña.";
      } finally {
        this.loading = false;
      }
    },
    async loadRecipients() {
      this.recipientsLoading = true;
      try {
        const response = await this.terrainApi.listRecipients(this.campaignId);
        this.recipients = (response.data && response.data.items) || [];
      } catch (error) {
        this.recipients = [];
      } finally {
        this.recipientsLoading = false;
      }
    },
    async loadResponseLock() {
      try {
        const response = await this.terrainApi.listResponses({ campaignId: this.campaignId, page: 1, size: 1 });
        const body = response.data || {};
        this.hasResponses = Number(body.total || 0) > 0 || ((body.items || []).length > 0);
      } catch (error) {
        // El backend mantiene el bloqueo autoritativo. Si esta lectura falla,
        // no se inhabilita a ciegas el formulario y el error de guardado
        // explicará la condición real al administrador.
        this.hasResponses = false;
      }
    },
    async loadNewMembers() {
      this.newMembersLoading = true;
      try {
        const response = await this.terrainApi.getNewMembers(this.campaignId);
        this.newMembers = (response.data && response.data.items) || [];
      } catch (error) {
        this.newMembers = [];
      } finally {
        this.newMembersLoading = false;
      }
    },
    // recipientLabel muestra la mejor identidad disponible: la Lista_
    // Efectiva_de_Destinatarios (a diferencia de Nuevos_Integrantes_
    // Detectados) no guarda username, solo las Fuentes_de_Asignación — una
    // fuente individual conserva el username tal cual; el resto solo
    // conserva nombres de Grupo/Cuadrilla, así que se listan como
    // respaldo.
    recipientLabel(recipient) {
	  if (recipient.email) return recipient.userId ? `${recipient.email} (usuario vinculado)` : recipient.email;
      const individual = recipient.sources.find((source) => source.type === "individual");
      if (individual) return individual.sourceNameSnapshot;
      const names = recipient.sources.map((source) => source.sourceNameSnapshot).filter(Boolean);
      return names.length ? `Vía ${names.join(', ')}` : recipient.userId;
    },
	async copyShareableLink() {
	  if (!this.shareableLink) return;
	  await navigator.clipboard.writeText(this.shareableLink);
	  this.$store.dispatch("toast/show", { message: "Enlace copiado.", type: "success" });
	},
	openShareModal() {
	  if (!this.canShareLink) return;
	  this.$modal.show("terrain-campaign-share-link");
	},
	closeShareModal() { this.$modal.hide("terrain-campaign-share-link"); },
	// Mismo patrón que TerrainCampaigns.vue::shareCampaignLink — el usuario
	// cancelando el cuadro de compartir nativo no es un error.
	async shareShareableLink() {
	  if (!this.canShareLink || !this.hasShareApi) return;
	  try {
	    await navigator.share({ title: this.name, text: `Responde la campaña de terreno: ${this.name}`, url: this.shareableLink });
	  } catch (error) { /* cancelado por el usuario */ }
	},
    confirmExpand() {
      this.$modal.show("dialog", {
        text: `¿Enviar a ${this.newMembers.length} nuevo${this.newMembers.length === 1 ? '' : 's'} integrante${this.newMembers.length === 1 ? '' : 's'}? Se les incorporará a la lista de destinatarios y se les avisará según la política de notificaciones configurada.`,
        buttons: [
          { title: "Cancelar" },
          { title: "Enviar", handler: () => { this.$modal.hide("dialog"); this.onExpand(); } },
        ],
      });
    },
    async onExpand() {
      this.expanding = true;
      try {
        await this.terrainApi.expandRecipients(this.campaignId);
        this.$store.dispatch("toast/show", { message: "Nuevos integrantes incorporados correctamente.", type: "success" });
        await Promise.all([this.loadRecipients(), this.loadNewMembers()]);
      } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || "No se pudo enviar a los nuevos integrantes.";
        this.$store.dispatch("toast/show", { message, type: "error" });
      } finally {
        this.expanding = false;
      }
    },
    startWithdraw(recipient) {
	  this.withdrawTargetId = recipient.id;
      this.withdrawReason = "";
    },
    cancelWithdraw() {
      this.withdrawTargetId = null;
      this.withdrawReason = "";
    },
    async confirmWithdraw(recipient) {
      if (!this.withdrawReason.trim()) {
        this.$store.dispatch("toast/show", { message: "El motivo del retiro es obligatorio.", type: "error" });
        return;
      }
      this.withdrawing = true;
      try {
		await this.terrainApi.withdrawRecipient(this.campaignId, recipient.id, this.withdrawReason.trim());
        this.$store.dispatch("toast/show", { message: "Destinatario retirado correctamente.", type: "success" });
        this.cancelWithdraw();
        await this.loadRecipients();
      } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || "No se pudo retirar al destinatario.";
        this.$store.dispatch("toast/show", { message, type: "error" });
      } finally {
        this.withdrawing = false;
      }
    },
    mapLabel(mapId) {
      const option = this.mapOptions.find((map) => map.id === mapId);
      return (option && option.label) || mapId;
    },
    // loadMapLayerSettings alimenta las tarjetas de "Estilo por mapa" para
    // cada mapa de destino que todavía no tiene entrada en mapLayerSettings
    // (incremental: si el usuario agrega un mapa a una selección que ya
    // tenía otros con ediciones sin guardar, no las pisa). La fuente
    // depende de si la capa ya existe de verdad en GeoServer:
    // - Publicada (terrainLayerId real): GET /maps/:mapId/section, igual
    //   que siempre — no existe un GET dedicado a un solo (mapa, capa).
    // - Borrador: destinationMapStyleDrafts, que ya vino con getCampaign()
    //   (loadCampaign) — sin red, sin fila real que consultar todavía.
    async loadMapLayerSettings() {
      const targets = this.destinationMapIds.filter((mapId) => !this.mapLayerSettings[mapId]);
      if (!targets.length) return;

      if (this.status === "published" && this.terrainLayerId) {
        await Promise.all(targets.map(async (mapId) => {
          try {
            const response = await this.terrainApi.getMapSection(mapId);
            const layers = (response.data && response.data.layers) || [];
            const entry = layers.find((layer) => layer.terrainLayerId === this.terrainLayerId);
            if (!entry) return;
            this.$set(this.mapLayerSettings, mapId, {
              visualGroupId: entry.visualGroup.id,
              visualGroupName: entry.visualGroup.name,
              visualGroupOrder: entry.visualGroup.order,
              layerOrder: entry.layerOrder,
              initialVisible: entry.initialVisible,
              color: entry.style.color || "#3388ff",
              size: entry.style.size || DEFAULT_TERRAIN_MARKER_SIZE,
              icon: entry.style.icon || ICON_CATALOG[0].value,
              opacity: entry.style.opacity != null ? entry.style.opacity : 1,
              resourceVersion: entry.resourceVersion,
              saving: false,
            });
          } catch (error) {
            // Si el mapa no tiene fila para esta capa (todavía no debería
            // pasar, la crea la publicación) simplemente no se ofrece editar.
          }
        }));
        return;
      }

      targets.forEach((mapId) => {
        const draft = this.mapStyleDrafts[mapId] || null;
        this.$set(this.mapLayerSettings, mapId, {
          visualGroupId: (draft && draft.visualGroupId) || "terrain",
          visualGroupName: (draft && draft.visualGroupName) || "Terreno",
          visualGroupOrder: (draft && draft.visualGroupOrder) || 0,
          layerOrder: (draft && draft.layerOrder) || 0,
          initialVisible: draft ? draft.initialVisible : true,
          color: (draft && draft.style && draft.style.color) || "#3388ff",
          size: (draft && draft.style && draft.style.size) || DEFAULT_TERRAIN_MARKER_SIZE,
          icon: (draft && draft.style && draft.style.icon) || ICON_CATALOG[0].value,
          opacity: draft && draft.style && draft.style.opacity != null ? draft.style.opacity : 1,
          saving: false,
        });
      });
    },
    // loadVisualGroupOptions arma el catálogo de "Grupo visual" para el
    // combobox creable, por mapa de destino: los grupos que ya usan otras
    // Capas_Terreno (de cualquier campaña) asignadas a ESE mapa
    // (GET /maps/:mapId/section, sin capability especial más allá de
    // sesión) — un grupo visual solo agrupa dentro de la lista de capas de
    // un mapa, así que el catálogo es por mapa, no global. Cacheado por
    // mapId; si el fetch falla, deja [] y no bloquea seguir escribiendo/
    // creando un grupo a mano.
    async loadVisualGroupOptions(mapId) {
      if (this.visualGroupOptions[mapId]) return;
      this.$set(this.visualGroupOptionsLoading, mapId, true);
      try {
        const response = await this.terrainApi.getMapSection(mapId);
        const layers = (response.data && response.data.layers) || [];
        const byId = new Map();
        layers.forEach((layer) => {
          const group = layer.visualGroup;
          if (group && group.id && !byId.has(group.id)) byId.set(group.id, { id: group.id, label: group.name || group.id });
        });
        this.$set(this.visualGroupOptions, mapId, Array.from(byId.values()));
      } catch (error) {
        this.$set(this.visualGroupOptions, mapId, []);
      } finally {
        this.$set(this.visualGroupOptionsLoading, mapId, false);
      }
    },
    // visualGroupOptionsFor: el valor YA seleccionado de un mapa (ej. el
    // default visualGroupId:"terrain" de un mapa recién agregado) puede no
    // estar entre los grupos "ya usados en este mapa" — sin esto el
    // combobox se vería vacío aunque el campo sí tenga un valor real.
    visualGroupOptionsFor(mapId) {
      const options = [...(this.visualGroupOptions[mapId] || [])];
      const settings = this.mapLayerSettings[mapId];
      if (settings && settings.visualGroupId && !options.some((option) => option.id === settings.visualGroupId)) {
        options.unshift({ id: settings.visualGroupId, label: settings.visualGroupName || settings.visualGroupId });
      }
      return options;
    },
    selectVisualGroup(mapId, groupId) {
      const option = this.visualGroupOptionsFor(mapId).find((candidate) => candidate.id === groupId);
      if (!option) return;
      this.mapLayerSettings[mapId].visualGroupId = option.id;
      this.mapLayerSettings[mapId].visualGroupName = option.label;
    },
    createVisualGroup(mapId, text) {
      const trimmed = text.trim();
      if (!trimmed) return;
      if (!this.visualGroupOptions[mapId]) this.$set(this.visualGroupOptions, mapId, []);
      const existing = this.visualGroupOptions[mapId].find((option) => option.label.toLowerCase() === trimmed.toLowerCase());
      const option = existing || { id: trimmed, label: trimmed };
      if (!existing) this.visualGroupOptions[mapId].push(option);
      this.mapLayerSettings[mapId].visualGroupId = option.id;
      this.mapLayerSettings[mapId].visualGroupName = option.label;
    },
    // saveMapLayerSettings solo se ofrece con la campaña ya publicada (ver
    // el v-if de la tarjeta): ahí sí existe la fila real
    // (PUT /layers/:id/maps/:mapId) para editar un mapa de forma
    // independiente. En borrador esa fila no existe todavía — el estilo
    // viaja junto con el resto de la campaña en el "Guardar" de abajo
    // (onSave(), que arma destinationMapStyleDrafts en buildPayload()), sin
    // un botón individual por tarjeta que sugiera lo contrario.
    async saveMapLayerSettings(mapId) {
      const settings = this.mapLayerSettings[mapId];
      if (!settings) return;
      settings.saving = true;
      try {
        const response = await this.terrainApi.updateMapLayerSettings(this.terrainLayerId, mapId, {
          visualGroupId: settings.visualGroupId,
          visualGroupName: settings.visualGroupName,
          visualGroupOrder: settings.visualGroupOrder,
          layerOrder: settings.layerOrder,
          initialVisible: settings.initialVisible,
          style: { color: settings.color, size: settings.size, icon: settings.icon, opacity: settings.opacity },
          expectedVersion: settings.resourceVersion,
        });
        settings.resourceVersion = response.data.resourceVersion;
        this.$store.dispatch("toast/show", { message: `Estilo guardado para ${this.mapLabel(mapId)}.`, type: "success" });
      } catch (error) {
        const data = error.response && error.response.data;
        const message = (data && Array.isArray(data.fieldErrors) && data.fieldErrors.length && data.fieldErrors[0].message)
          || (data && data.message) || "No se pudo guardar el estilo.";
        this.$store.dispatch("toast/show", { message, type: "error" });
      } finally {
        settings.saving = false;
      }
    },
    buildPayload() {
      const payload = {
        name: this.name.trim(),
        templateVersionId: this.templateVersionId,
        // startDay/endDay ya son YYYY-MM-DD (formato nativo de
        // TerrainDateRangePicker) — se mandan tal cual, sin conversión: el
        // backend calcula los instantes UTC en su zona fija.
        startAt: this.startDay,
        closeAt: this.endDay,
        requiresApproval: this.requiresApproval,
		sharedAccessMode: this.sharedAccessMode,
        responsePolicy: this.maxPerRespondent ? { maxPerRespondent: Number(this.maxPerRespondent) } : {},
        selectedSources: this.selectedSources,
        destinationMapIds: this.destinationMapIds,
        notificationPolicy: this.notificationPolicy,
        destinationMapStyleDrafts: this.buildMapStyleDraftsPayload(),
		prefill: { enabled: this.prefillEnabled, identifierQuestionId: this.prefillEnabled ? this.identifierQuestionId : '', fields: this.prefillEnabled ? Object.keys(this.prefillPolicies).map((questionId) => ({ questionId, editable: this.prefillPolicies[questionId] })) : [] },
      };
      if (this.isEditing) payload.expectedVersion = this.resourceVersion;
      return payload;
    },
	onPrefillToggle() {
	  if (this.prefillEnabled) {
	    this.sharedAccessMode = 'restricted';
	    if (this.identifierQuestionId) this.$set(this.prefillPolicies, this.identifierQuestionId, true);
	    this.scheduleAutoSavePrefill();
	    return;
	  }
	  this.$modal.show('dialog', {
	    text: 'Se eliminará el formato de prellenado configurado (pregunta identificadora y campos prellenables) al guardar. Las respuestas ya enviadas no se ven afectadas: no dependen de si fueron prellenadas.',
	    buttons: [
	      { title: 'Cancelar', handler: () => { this.$modal.hide('dialog'); this.prefillEnabled = true; } },
	      { title: 'Deshabilitar', handler: () => { this.$modal.hide('dialog'); } },
	    ],
	  });
	},
	scheduleAutoSavePrefill() {
	  clearTimeout(this._prefillAutoSaveTimer);
	  this._prefillAutoSaveTimer = setTimeout(this.autoSavePrefill, 500);
	},
	async autoSavePrefill() {
	  // Solo dispara cuando la config quedó completa (habilitado + pregunta
	  // identificadora elegida); desmarcar el checkbox sigue requiriendo un
	  // Guardar manual, igual que cualquier otro cambio de la campaña. El
	  // resto de las preguntas prellenables (editable/bloqueado) ya no se
	  // eligen acá — Big Boss, 2026-09-09: eso vive en Administrar casos.
	  if (!this.isEditing || !this.prefillEnabled || !this.identifierQuestionId) return;
	  if (this.disabled || this.saving || !this.hasUnsavedChanges) return;
	  await this.onSave();
	},
    // buildMapStyleDraftsPayload proyecta mapLayerSettings (estado editable
    // del formulario) al shape que espera destinationMapStyleDrafts —
    // inofensivo enviarlo incluso ya publicada (el backend solo lo lee al
    // publicar, ver campaign_publication_repo.go; después queda sin uso).
    buildMapStyleDraftsPayload() {
      const drafts = {};
      this.destinationMapIds.forEach((mapId) => {
        const settings = this.mapLayerSettings[mapId];
        if (!settings) return;
        drafts[mapId] = {
          visualGroupId: settings.visualGroupId,
          visualGroupName: settings.visualGroupName,
          visualGroupOrder: settings.visualGroupOrder,
          layerOrder: settings.layerOrder,
          initialVisible: settings.initialVisible,
          style: { color: settings.color, size: settings.size, icon: settings.icon, opacity: settings.opacity },
        };
      });
      return drafts;
    },
    validateLocalFields() {
      if (!this.name.trim()) return "El nombre de la campaña es obligatorio.";
      if (!this.templateVersionId) return "Seleccione una versión de plantilla publicada.";
      if (!this.startDay || !this.endDay) return "Indique fecha de inicio y de cierre.";
      if (this.endDay < this.startDay) return "El cierre debe ser igual o posterior al inicio.";
      return "";
    },
    // persistCampaign hace el create/update que comparten "Guardar" y
    // "Guardar y publicar" — este último ya no depende de un click previo en
    // "Guardar": persiste el borrador vigente y devuelve el id/resourceVersion
    // frescos para encadenar el publish en el mismo paso.
    async persistCampaign() {
      const payload = this.buildPayload();
      const response = this.isEditing
        ? await this.terrainApi.updateCampaign(this.campaignId, payload)
        : await this.terrainApi.createCampaign(payload);
      const body = response.data;
      this.resourceVersion = body.resourceVersion;
      this.status = body.status;
      // Recaptura la instantánea "ya guardado" con el resourceVersion recién
      // recibido, para que Guardar vuelva a quedar deshabilitado hasta el
      // próximo cambio real. Al crear (isEditing todavía false acá, cambia
      // recién con la navegación de más abajo) esta captura es transitoria:
      // el watcher de $route.params.campaignId dispara initialize(), que
      // vuelve a capturar con expectedVersion ya incluido.
      this.captureBaseline();
      if (!this.isEditing) {
        this.$router.replace({ name: "terrain-campaign-edit", params: { campaignId: body.id } });
      }
      return body;
    },
    async onSave() {
      this.errorMessage = "";
      this.fieldErrorMessages = [];
      const localError = this.validateLocalFields();
      if (localError) {
        this.errorMessage = localError;
        return;
      }
      this.saving = true;
      try {
        await this.persistCampaign();
        this.$store.dispatch("toast/show", { message: "Campaña guardada correctamente.", type: "success" });
        // Bug reportado por Big Boss: guardar un cambio de Destinatarios
        // (p.ej. agregar un Grupo) no refrescaba "Nuevos integrantes
        // detectados" - antes solo se cargaba una vez, en loadCampaign(),
        // así que había que recargar toda la página para ver la opción de
        // "Enviar a nuevos integrantes". Si el guardado hizo aparecer
        // nuevos integrantes, se ofrece enviarles de inmediato en vez de
        // dejar que el admin lo descubra por su cuenta.
        if (this.status === "published") {
          const previousNewMembersCount = this.newMembers.length;
          await Promise.all([this.loadRecipients(), this.loadNewMembers()]);
          if (this.newMembers.length > previousNewMembersCount) {
            this.confirmExpand();
          }
        }
      } catch (error) {
        this.applyServerErrors(error, "No se pudo guardar la campaña.");
      } finally {
        this.saving = false;
      }
    },
    confirmPublish() {
      const localError = this.validateLocalFields();
      if (localError) {
        this.errorMessage = localError;
        return;
      }
      this.$modal.show("dialog", {
        text: "¿Guardar y publicar esta campaña? Se congelará la lista de destinatarios y quedará visible a los respondentes.",
        buttons: [
          { title: "Cancelar" },
          { title: "Guardar y publicar", handler: () => { this.$modal.hide("dialog"); this.onPublish(); } },
        ],
      });
    },
    async onPublish() {
      this.publishing = true;
      this.errorMessage = "";
      this.fieldErrorMessages = [];
      try {
        const saved = await this.persistCampaign();
        const response = await this.terrainApi.publishCampaign(saved.id, saved.resourceVersion);
        const body = response.data;
        this.resourceVersion = body.resourceVersion;
        this.status = body.status;
        this.$store.dispatch("toast/show", { message: "Campaña guardada y publicada correctamente.", type: "success" });
        // Mismo gap que en onSave(): sin esto, la sección "Destinatarios de
        // la campaña publicada" queda mostrando "Todavía no hay
        // destinatarios incorporados" hasta recargar, aunque el snapshot
        // recién se congeló con la primera publicación.
        if (this.status === "published") {
          await this.loadRecipients();
        }
      } catch (error) {
        this.applyServerErrors(error, "No se pudo guardar y publicar la campaña.");
      } finally {
        this.publishing = false;
      }
    },
    confirmClose() {
      this.$modal.show("dialog", {
        text: "¿Cerrar esta campaña? Ya no se aceptarán nuevos envíos.",
        buttons: [
          { title: "Cancelar" },
          { title: "Cerrar campaña", handler: () => { this.$modal.hide("dialog"); this.onClose(); } },
        ],
      });
    },
    async onClose() {
      this.closing = true;
      this.errorMessage = "";
      try {
        const response = await this.terrainApi.closeCampaign(this.campaignId, this.resourceVersion);
        const body = response.data;
        this.resourceVersion = body.resourceVersion;
        this.status = body.status;
        this.$store.dispatch("toast/show", { message: "Campaña cerrada correctamente.", type: "success" });
      } catch (error) {
        this.applyServerErrors(error, "No se pudo cerrar la campaña.");
      } finally {
        this.closing = false;
      }
    },
    // onDuplicate crea una campaña NUEVA en borrador copiando la
    // configuración vigente (incluida ya cualquier edición sin guardar) —
    // sigue siendo la única forma de "reabrir" una campaña publicada/cerrada
    // con una vigencia nueva; ampliar/retirar destinatarios de la campaña ya
    // publicada usa en cambio la sección de más abajo (tareas 4.4-4.6).
    async onDuplicate() {
      this.duplicating = true;
      this.errorMessage = "";
      this.fieldErrorMessages = [];
      try {
        const payload = this.buildPayload();
        delete payload.expectedVersion;
        payload.name = `${(this.name || "").trim()} (copia)`;
        const response = await this.terrainApi.createCampaign(payload);
        const body = response.data;
        this.$store.dispatch("toast/show", { message: "Campaña duplicada como nuevo borrador.", type: "success" });
        this.$router.push({ name: "terrain-campaign-edit", params: { campaignId: body.id } });
      } catch (error) {
        this.applyServerErrors(error, "No se pudo duplicar la campaña.");
      } finally {
        this.duplicating = false;
      }
    },
    applyServerErrors(error, fallbackMessage) {
      const data = error.response && error.response.data;
      if (data && Array.isArray(data.fieldErrors) && data.fieldErrors.length) {
        this.fieldErrorMessages = data.fieldErrors.map((fieldError) => fieldError.message);
        this.errorMessage = data.message || "Revise los campos indicados.";
      } else if (data && data.code === "TERRAIN_STALE_RESOURCE") {
        this.errorMessage = "La campaña fue modificada por otra solicitud; recargue e intente de nuevo.";
      } else {
        this.errorMessage = (data && data.message) || fallbackMessage;
      }
      this.$store.dispatch("toast/show", { message: this.errorMessage, type: "error" });
    },
    dateLabel(value) {
      if (!value) return "Sin fecha";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? "Sin fecha" : date.toLocaleString();
    },
  },
};
</script>

<style scoped lang="scss">
.terrain-campaign-edit__body {
  display: grid;
  gap: var(--terrain-space-xl);
  margin: 0 auto;
  max-width: 860px;
  width: 95%;
}

.terrain-campaign-edit__header {
  align-items: center;
  background: var(--color-white);
  border-bottom: 1px solid var(--color-line);
  display: flex;
  flex-wrap: wrap;
  gap: var(--terrain-space-md);
  justify-content: space-between;
  padding: var(--terrain-space-sm) 0;
  position: sticky;
  top: 0;
  z-index: 3;
}

.terrain-campaign-edit__header-status { display: flex; }

.terrain-campaign-edit__field {
  display: grid;
  gap: var(--terrain-space-xs);
  color: var(--color-navy);
  font-weight: 600;
  font-size: var(--terrain-font-caption);
}

.terrain-campaign-edit__field--narrow { max-width: 320px; }

.terrain-campaign-edit__hint {
  color: var(--color-ghost);
  font-weight: 400;
}

.terrain-campaign-edit__field input {
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  font: inherit;
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  background: var(--color-white);
  color: var(--color-graphite);
  transition: border-color var(--terrain-transition), box-shadow var(--terrain-transition);

  &:hover { border-color: var(--color-steel); }
  &:focus { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); outline: none; }
  &:disabled { background: var(--color-mist); cursor: not-allowed; }
}

// El padding heredado de arriba deja la muestra de color de un
// input[type=color] reducida a una línea: el swatch nativo se dibuja dentro
// del content-box, así que con ese padding solo queda visible el borde.
.terrain-campaign-edit__field input[type="color"] {
  width: 3rem;
  padding: 0;
}

.terrain-campaign-edit__share-link {
  flex: 1;
  min-width: 0;
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  background: var(--color-mist);
  color: var(--color-graphite);
  font: inherit;
}

.terrain-campaign-edit__icon-btn {
  align-items: center;
  background: var(--color-white);
  border: 1px solid var(--color-navy);
  border-radius: 50%;
  color: var(--color-navy);
  cursor: pointer;
  display: flex;
  height: 2.25rem;
  width: 2.25rem;
  justify-content: center;
  transition: background-color var(--terrain-transition), color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-navy); color: var(--color-white); }
  &:disabled { background: var(--color-white); border-color: var(--color-line); color: var(--color-ghost); cursor: not-allowed; }
}

.terrain-campaign-edit__share-modal {
  display: grid;
  gap: var(--terrain-space-md);
  padding: var(--terrain-space-lg);

  h2 { margin: 0; color: var(--color-navy); }
}

.terrain-campaign-edit__share-row { display: flex; gap: var(--terrain-space-sm); }

.terrain-campaign-edit__preview-action {
  align-items: flex-end;
  display: flex;
  min-height: 4.25rem;
}

.terrain-campaign-edit__preview-button {
  align-items: center;
  display: inline-flex;
  gap: var(--terrain-space-xs);
  justify-content: center;
  white-space: nowrap;
}

.terrain-campaign-edit__template-preview {
  background: var(--color-mist);
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;
  min-height: 0;
}

.terrain-campaign-edit__template-preview-header {
  align-items: center;
  background: var(--color-white);
  border-bottom: 1px solid var(--color-line);
  display: flex;
  gap: var(--terrain-space-md);
  justify-content: space-between;
  padding: var(--terrain-space-md) var(--terrain-space-lg);

  h2 { color: var(--color-navy); margin: 0; }
  p { color: var(--color-ghost); margin: var(--terrain-space-xs) 0 0; }
}

.terrain-campaign-edit__template-preview-body {
  min-height: 0;
  overflow-y: auto;
  padding: var(--terrain-space-lg);
}

.terrain-campaign-edit__template-preview-error {
  align-items: flex-start;
  color: var(--color-danger);
  display: grid;
  gap: var(--terrain-space-sm);
  justify-items: start;

  p { margin: 0; }
}

.terrain-campaign-edit__map-modal-actions { display: flex; justify-content: flex-end; gap: var(--terrain-space-sm); }

.terrain-campaign-edit__map-modal-cancel {
  background: var(--color-white);
  border: 1px solid var(--color-navy);
  border-radius: var(--terrain-radius-xl);
  color: var(--color-navy);
  cursor: pointer;
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-lg);
  transition: background-color var(--terrain-transition);

  &:hover { background: var(--color-mist); }
}

.terrain-campaign-edit__row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--terrain-space-lg);
  align-items: end;
}

.terrain-campaign-edit__fieldset {
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-lg);
  padding: var(--terrain-space-lg);
  display: grid;
  gap: var(--terrain-space-md);

  legend {
    padding: 0 var(--terrain-space-xs);
    color: var(--color-navy);
    font-weight: 700;
  }
}

.terrain-campaign-edit__accordion-toggle {
  display: inline-flex;
  align-items: center;
  gap: var(--terrain-space-xs);
  background: none;
  border: none;
  padding: 0;
  color: inherit;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.expand-enter-active {
  transition: all 0.3s ease;
  max-height: 1000px;
  overflow: hidden;
}

.expand-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
  max-height: 1000px;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.terrain-campaign-edit__notifications {
  border-collapse: collapse;
  width: 100%;

  th, td {
    border-bottom: 1px solid var(--color-line);
    padding: var(--terrain-space-sm);
    text-align: left;
  }

  th { color: var(--color-navy); }
  th:not(:first-child), td:not(:first-child) { text-align: center; }
}

.terrain-campaign-edit__new-members {
  background: var(--color-orange-light);
  border-radius: var(--terrain-radius-md);
  padding: var(--terrain-space-md);
  margin-bottom: var(--terrain-space-md);

  ul { margin: var(--terrain-space-xs) 0; padding-left: var(--terrain-space-lg); }
}

.terrain-campaign-edit__withdraw-row {
  background: var(--color-mist);
  display: grid;
  gap: var(--terrain-space-sm);
  padding: var(--terrain-space-md) !important;
  text-align: left !important;

  textarea { font: inherit; padding: var(--terrain-space-sm); resize: vertical; width: 100%; }
}

.terrain-campaign-edit__map-style-card {
  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  display: grid;
  gap: var(--terrain-space-sm);
  padding: var(--terrain-space-md);

  & + & { margin-top: var(--terrain-space-sm); }

  h4 { color: var(--color-navy); margin: 0; }
}

.terrain-campaign-edit__checkbox-field {
  align-items: center;
  color: var(--color-graphite);
  display: flex;
  gap: var(--terrain-space-xs);
}


.terrain-campaign-edit__actions { display: flex; flex-wrap: wrap; gap: var(--terrain-space-md); }

.terrain-campaign-edit__primary,
.terrain-campaign-edit__secondary {
  border: 0;
  border-radius: var(--terrain-radius-xl);
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-lg);
  transition: background-color var(--terrain-transition);

  &:disabled { cursor: not-allowed; opacity: .6; }
}

.terrain-campaign-edit__primary {
  background: var(--color-navy);
  color: var(--color-white);

  &:hover:not(:disabled) { background: var(--color-steel); }
}

.terrain-campaign-edit__secondary {
  background: var(--color-white);
  border: 1px solid var(--color-navy);
  color: var(--color-navy);

  &:hover:not(:disabled) { background: var(--color-mist); }
}

.terrain-campaign-edit__error {
  margin: 0;
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  background: var(--terrain-color-danger-light);
  border-left: 4px solid var(--color-danger);
  border-radius: var(--terrain-radius-sm);
  color: var(--color-danger);
  list-style: none;
}

.terrain-campaign-edit__status { color: var(--color-ghost); margin: 0; }

@media (max-width: 640px) {
  .terrain-campaign-edit__row { grid-template-columns: 1fr; }
}
</style>
